import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, HandCoins, Scale } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { fmtMoney } from "@/lib/expenseConstants";
import { UserAvatar } from "@/components/Avatars";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const BalancesTab = ({ workspaceId }) => {
  const qc = useQueryClient();
  const [settle, setSettle] = useState(null);
  const [settleAmount, setSettleAmount] = useState("");

  const { data: balances = [] } = useQuery({
    queryKey: ["balances", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/balances`)).data,
  });
  const { data: settlements = [] } = useQuery({
    queryKey: ["settlements", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/settlements`)).data,
  });

  const openSettle = (debt, currency) => {
    setSettle({ ...debt, currency });
    setSettleAmount(String(debt.amount));
  };

  const confirmSettle = async () => {
    const amt = parseFloat(settleAmount);
    if (!amt || amt <= 0) return toast.error("Importe inválido");
    try {
      await api.post(`/workspaces/${workspaceId}/settlements`, {
        from_profile_id: settle.from_id, to_profile_id: settle.to_id,
        amount: amt, currency: settle.currency,
      });
      qc.invalidateQueries({ queryKey: ["balances", workspaceId] });
      qc.invalidateQueries({ queryKey: ["settlements", workspaceId] });
      toast.success("Liquidación registrada");
      setSettle(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const hasDebts = balances.some((b) => b.debts.length > 0);

  return (
    <div className="space-y-6" data-testid="balances-tab">
      {!hasDebts ? (
        <Card className="p-10 text-center border-dashed">
          <Scale className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Todo en paz ✨ Nadie debe nada a nadie.</p>
        </Card>
      ) : (
        balances.map((group) => group.debts.length > 0 && (
          <div key={group.currency} className="space-y-3">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
              Deudas pendientes ({group.currency})
            </h3>
            {group.debts.map((d, i) => (
              <Card key={`${d.from_id}-${d.to_id}`} className="p-4 flex items-center gap-3" data-testid={`debt-row-${d.from_id}-${d.to_id}`}>
                <UserAvatar name={d.from_name} color={d.from_color} size="sm" />
                <span className="text-sm font-medium">{d.from_name}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <UserAvatar name={d.to_name} color={d.to_color} size="sm" />
                <span className="text-sm font-medium flex-1">{d.to_name}</span>
                <span className="font-heading font-bold" data-testid={`debt-amount-${i}`}>{fmtMoney(d.amount, group.currency)}</span>
                <Button size="sm" variant="outline" data-testid={`settle-btn-${d.from_id}-${d.to_id}`}
                  onClick={() => openSettle(d, group.currency)}>
                  <HandCoins className="h-3.5 w-3.5 mr-1" /> Liquidar
                </Button>
              </Card>
            ))}
            <div className="flex flex-wrap gap-2">
              {group.balances.map((b) => (
                <span key={b.profile_id} data-testid={`net-chip-${b.profile_id}`}
                  className={`text-xs rounded-full px-3 py-1.5 border flex items-center gap-1.5 ${b.net > 0 ? "text-[#1E8E3E] border-[#1E8E3E]/30 bg-[#1E8E3E]/5" : "text-destructive border-destructive/30 bg-destructive/5"}`}>
                  <UserAvatar name={b.name} color={b.avatar_color} size="sm" className="!h-4 !w-4 !text-[8px]" />
                  {b.name}: {b.net > 0 ? "+" : ""}{fmtMoney(b.net, group.currency)}
                </span>
              ))}
            </div>
          </div>
        ))
      )}

      <div className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Historial de liquidaciones</h3>
        {settlements.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay liquidaciones registradas.</p>
        ) : (
          <Card className="divide-y divide-border" data-testid="settlements-history">
            {settlements.map((s) => (
              <div key={s.id} className="p-3.5 flex items-center gap-3 text-sm" data-testid={`settlement-row-${s.id}`}>
                <HandCoins className="h-4 w-4 text-[#1E8E3E] shrink-0" />
                <p className="flex-1">
                  <b>{s.from_profile_name}</b> pagó <b>{fmtMoney(s.amount, s.currency)}</b> a <b>{s.to_profile_name}</b>
                  {s.note && <span className="text-muted-foreground"> · {s.note}</span>}
                </p>
                <span className="text-xs text-muted-foreground">{timeAgo(s.created_at)}</span>
              </div>
            ))}
          </Card>
        )}
      </div>

      <Dialog open={!!settle} onOpenChange={(o) => !o && setSettle(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="settle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar liquidación</DialogTitle>
            <DialogDescription>
              {settle && <>{settle.from_name} paga a {settle.to_name}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Importe ({settle?.currency})</Label>
              <Input type="number" min="0" step="0.01" data-testid="settle-amount-input"
                value={settleAmount} onChange={(e) => setSettleAmount(e.target.value)} />
            </div>
            <Button className="w-full" data-testid="settle-confirm-btn" onClick={confirmSettle}>
              Confirmar pago
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
